import React from "react";
import { Loader2, Globe } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import ConfirmDialog from "@/components/ConfirmDialog";
import { useJobs } from "@/contexts/JobsContext";

// "Enrich emails (sites)" — dry-runs the personal-website enrichment,
// shows a cost preview, then launches on confirm. Backed by
// `POST /realtors/enrich-personal-websites` (dry_run flag).
//
// When `selectedIds` is non-empty, both the dry-run AND launch send
// `realtor_ids: [...]` so only the picked rows get processed. The
// button label flips to "Enrich N sites" so the scope is obvious.
export default function EnrichPersonalSitesButton({ selectedIds = [] }) {
  // {candidate_count, estimated_credits, firecrawl_remaining, ok_to_proceed}
  const [plan, setPlan] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const { track: trackJob } = useJobs();
  const scoped = Array.isArray(selectedIds) && selectedIds.length > 0;

  const planIt = async () => {
    setBusy(true);
    try {
      const body = { dry_run: true };
      if (scoped) body.realtor_ids = selectedIds;
      const { data } = await api.post("/realtors/enrich-personal-websites", body);
      setPlan(data);
      setOpen(true);
    } catch (e) {
      toast.error("Cost preview failed", { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  const launch = async () => {
    setBusy(true);
    try {
      const body = { dry_run: false };
      if (scoped) body.realtor_ids = selectedIds;
      const { data } = await api.post("/realtors/enrich-personal-websites", body);
      if (data.job_id) {
        trackJob(data.job_id, {
          type: "enrich_personal_websites",
          label: `${data.candidate_count} sites`,
        });
      }
      toast.success("Enrichment started", {
        description: `${data.candidate_count} realtors · est. ${data.estimated_credits} Firecrawl credits`,
      });
      setOpen(false);
      setPlan(null);
    } catch (e) {
      toast.error("Launch failed", { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={planIt}
        disabled={busy}
        className="h-8 px-2.5 text-xs"
        data-testid="enrich-personal-websites-button"
        title={
          scoped
            ? `Scrape the ${selectedIds.length} selected realtor sites for missing emails / extra socials`
            : "Use Firecrawl to scrape personal websites and fill in missing emails / extra socials"
        }
      >
        {busy ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <Globe className="h-4 w-4 mr-2" />
        )}
        {scoped ? `Enrich ${selectedIds.length} sites` : "Enrich emails (sites)"}
      </Button>
      <ConfirmDialog
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={launch}
        title="Enrich emails from personal websites?"
        description={
          plan ? (
            <div className="text-sm space-y-2">
              <p>
                Targets: <b>{plan.candidate_count}</b>{" "}
                {scoped
                  ? "selected realtor(s) with a personal website"
                  : "realtors with a website but no email"}
                .
              </p>
              <p>
                Estimated Firecrawl credits: <b>{plan.estimated_credits}</b>
                {typeof plan.firecrawl_remaining === "number" ? (
                  <>
                    {" "}· remaining this month: <b>{plan.firecrawl_remaining}</b>
                  </>
                ) : null}
              </p>
              {!plan.ok_to_proceed ? (
                <p className="text-red-700 text-xs">
                  Not enough Firecrawl credits remaining. Raise the cap in Settings → Scraping,
                  or wait for the monthly rollover.
                </p>
              ) : (
                <p className="text-xs text-slate-500">
                  Firecrawl will visit each personal site and extract email, bio, extra social
                  URLs. User-edited fields are never overwritten.
                </p>
              )}
            </div>
          ) : (
            ""
          )
        }
        confirmLabel="Run enrichment"
        confirmDisabled={!plan || !plan.ok_to_proceed}
        testid="enrich-personal-websites-confirm"
      />
    </>
  );
}
